/* eslint-disable no-console */
/* eslint-disable no-alert */
import React, { useState } from 'react';
import axios from 'supports/api';
import 'react-datepicker/dist/react-datepicker.css';
import PriceInputModal from '../PriceInputModal';
import PromoInputModal from '../PromoInputModal';
import {
  getValidationFields,
  getInvalidFieldNames,
  buildSchedulePayload,
} from './utils';
import useScheduleResources from './useScheduleResources';
import useScheduleForm from './useScheduleForm';
import usePromoDates from './usePromoDates';
import PlaceTimeCard from './sections/PlaceTimeCard';
import PriceCard from './sections/PriceCard';
import SpeakersSection from './sections/SpeakersSection';
import WorkshopDetailCard from './sections/WorkshopDetailCard';
import SubmitButtons from './sections/SubmitButtons';
import RepeatingHtmlList from './fields/RepeatingHtmlList';

// id 31 refers to Training Camp program_category
const TRAINING_CAMP_CATEGORY_ID = 31;

const getIdByName = (collection, name) => {
  return collection?.find((item) => item.name === name)?.id ?? null;
};

const selectPatch = (value) => ({
  value,
  valid: value !== 0,
  invalid: value === 0,
});

const ScheduleForm = ({
  edit,
  programId,
  programHeaderId,
  programCategoryType,
  programCategoryId,
  isMultipleCurrency,
  toggle,
  refresh,
}) => {
  const eventCategoryCheck = programCategoryType?.toLowerCase() === 'event';
  const trainingCampCategoryCheck =
    programCategoryId === TRAINING_CAMP_CATEGORY_ID;

  const resources = useScheduleResources();
  const form = useScheduleForm({ edit, programId });
  const { fields, setField, setFields } = form;
  const promoDates = usePromoDates(fields, setField, setFields);

  const [isLoading, setIsLoading] = useState(false);
  const [isInHotelTrainingSelected, setIsInHotelTrainingSelected] = useState(
    false,
  );
  const [selectedSpeakerId, setSelectedSpeakerId] = useState('none');
  const [speakerForm, setSpeakerForm] = useState({
    open: false,
    editMode: false,
    speakerData: null,
  });
  const [priceInputModalOpen, setPriceInputModalOpen] = useState(false);
  const [promoInputModalOpen, setPromoInputModalOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState({});
  const [selectedPromo, setSelectedPromo] = useState({});

  // Picking a study method cascades into branch/schedule/venue defaults,
  // looked up by name from the fetched reference lists.
  const onStudyMethodChange = (rawValue) => {
    const selectedStudyMethodId = parseInt(rawValue, 10);
    const { studyMethods, branches, studySchedules } = resources;

    const livestreamClassMethod = getIdByName(studyMethods, 'Livestream Class');
    const videoLearningMethod = getIdByName(studyMethods, 'Video Learning');
    const inHotelTrainingMethod = getIdByName(
      studyMethods,
      'In-Hotel Training',
    );

    const livestreamClassBranch = getIdByName(
      branches,
      'Purwadhika Livestream Class',
    );
    const trainingCampBranch = getIdByName(branches, 'Training Camp Branch');
    const videoLearningBranch = getIdByName(
      branches,
      'Purwadhika Online Learning',
    );

    const videoLearningSchedule = getIdByName(
      studySchedules,
      'Purwadhika Video Learning',
    );

    const updates = {
      branchId: 0,
      study_scheduleId: 0,
      isInHotelTrainingSelected: false,
    };

    if (selectedStudyMethodId === livestreamClassMethod) {
      updates.branchId = livestreamClassBranch;
    } else if (selectedStudyMethodId === videoLearningMethod) {
      updates.study_scheduleId = videoLearningSchedule;
      updates.branchId = videoLearningBranch;
    } else if (selectedStudyMethodId === inHotelTrainingMethod) {
      updates.branchId = trainingCampBranch;
      updates.isInHotelTrainingSelected = true;
    }

    setFields({
      study_methodId: selectPatch(selectedStudyMethodId),
      branchId: selectPatch(updates.branchId),
      study_scheduleId: selectPatch(updates.study_scheduleId),
      venueId: selectPatch(0),
    });
    setIsInHotelTrainingSelected(updates.isInHotelTrainingSelected);
  };

  const onActionSpeaker = (editMode = false, speakerData = null) => {
    setSpeakerForm((prev) => ({ open: !prev.open, editMode, speakerData }));
  };

  const onAddSpeaker = () => {
    if (selectedSpeakerId === 'none') {
      alert('Please pick a speaker');
      return;
    }

    const speaker = resources.speakers[selectedSpeakerId];
    const alreadyAdded = fields.speakerIds.value.some(
      (item) => item.id === speaker.id,
    );

    if (alreadyAdded) {
      alert('Speaker already added');
      return;
    }

    setField('speakerIds', { value: [...fields.speakerIds.value, speaker] });
    setSelectedSpeakerId('none');
  };

  const onDeleteSpeaker = (index) => {
    setField('speakerIds', {
      value: fields.speakerIds.value.filter((item, i) => i !== index),
    });
  };

  const handleAddPrices = (newPrice) => {
    const current = fields.programPrices.value;
    const foundSameCurrencyIndex = current.findIndex(
      (price) => price.currency.id === newPrice.currency.id,
    );

    if (foundSameCurrencyIndex < 0) {
      setField('programPrices', {
        value: [...current, newPrice],
        invalid: false,
      });
    } else {
      setField('programPrices', {
        value: current.map((price, i) =>
          i === foundSameCurrencyIndex ? newPrice : price,
        ),
      });
    }
  };

  const handleDeletePrices = (index) => {
    setField('programPrices', {
      value: fields.programPrices.value.filter((price, i) => i !== index),
    });
  };

  const handleAddAdditionalPromos = (newPromo, selectedPromoIndex) => {
    const newPromoWithProgramId = { ...newPromo, programId };
    const current = fields.programAdditionalPromos.value;

    setField('programAdditionalPromos', {
      value:
        selectedPromoIndex === null
          ? [...current, newPromoWithProgramId]
          : current.map((promo, i) =>
              i === selectedPromoIndex ? newPromoWithProgramId : promo,
            ),
      invalid: false,
    });
  };

  // kept for the temporarily hidden promos table in PriceCard
  // eslint-disable-next-line no-unused-vars
  const handleDeletePromo = (index) => {
    setField('programAdditionalPromos', {
      value: fields.programAdditionalPromos.value.filter(
        (promo, i) => i !== index,
      ),
      invalid: false,
    });
  };

  const validateForm = () => {
    let result = 0;

    // validate programPrices if multiple currency is supported
    if (isMultipleCurrency && fields.programPrices.value.length === 0) {
      setField('programPrices', { invalid: true });
      result += 1;
    }

    const invalidNames = getInvalidFieldNames(
      getValidationFields(fields, {
        eventCategoryCheck,
        trainingCampCategoryCheck,
        isInHotelTrainingSelected,
      }),
    );
    form.markInvalid(invalidNames);
    result += invalidNames.length;

    if (result > 0) {
      throw new Error('There are invalid fields');
    }
  };

  const submitSchedule = async () => {
    try {
      validateForm();

      if (!window.confirm('Are you sure?')) return;

      setIsLoading(true);
      const data = buildSchedulePayload(fields, { eventCategoryCheck });

      if (edit) {
        await axios.put(
          eventCategoryCheck
            ? // for program category Workshop (4) & Event (5)
              `/admin/program/schedule/${programId}`
            : `/v2/program/${programId}`,
          data,
        );
        alert('Update Schedule Success');
        setIsLoading(false);
      } else if (eventCategoryCheck) {
        // for program category Workshop (4) & Event (5)
        await axios.post(`/admin/create/schedule/${programHeaderId}`, data);
      } else {
        await axios.post(`/v2/program/${programHeaderId}`, data);
      }

      form.resetForm();
      toggle();
      refresh();
    } catch (err) {
      setIsLoading(false);
      alert(err);
      console.log(err);
    }
  };

  const onCancel = () => {
    form.resetForm();
    toggle();
  };

  return (
    <div className="white-box py-2 container">
      <div className="row">
        <div className="col-12">
          <PlaceTimeCard
            fields={fields}
            resources={resources}
            eventCategoryCheck={eventCategoryCheck}
            trainingCampCategoryCheck={trainingCampCategoryCheck}
            isInHotelTrainingSelected={isInHotelTrainingSelected}
            onStudyMethodChange={onStudyMethodChange}
            handleSelect={form.handleSelect}
            handleInput={form.handleInput}
            handleStartEndDate={form.handleStartEndDate}
            handleStartTime={form.handleStartTime}
            handleEndTime={form.handleEndTime}
            handlePracticeStartTime={form.handlePracticeStartTime}
            handlePracticeEndTime={form.handlePracticeEndTime}
          />
          <PriceCard
            fields={fields}
            isMultipleCurrency={isMultipleCurrency}
            handleInput={form.handleInput}
            handleCurrencyInput={form.handleCurrencyInput}
            onAddPriceClick={() => setPriceInputModalOpen((prev) => !prev)}
            onEditPrice={(price) => {
              setSelectedPrice(price);
              setPriceInputModalOpen((prev) => !prev);
            }}
            onDeletePrice={handleDeletePrices}
            promoDates={promoDates}
          />
          {eventCategoryCheck && (
            <SpeakersSection
              speakers={resources.speakers}
              selectedSpeakerId={selectedSpeakerId}
              onSelectSpeaker={setSelectedSpeakerId}
              onAddSpeaker={onAddSpeaker}
              selectedSpeakerList={fields.speakerIds.value}
              onDeleteSpeaker={onDeleteSpeaker}
              onActionSpeaker={onActionSpeaker}
              speakerFormOpen={speakerForm.open}
              speakerFormEdit={speakerForm.editMode}
              speakerData={speakerForm.speakerData}
              getSpeakers={resources.getSpeakers}
            />
          )}
        </div>
        <div className="col-12">
          {eventCategoryCheck && (
            <WorkshopDetailCard
              content={fields.content}
              notes={fields.notes}
              regionType={fields.regionType}
              handleInput={form.handleInput}
              handleOption={form.handleOption}
            />
          )}
          {eventCategoryCheck && (
            <RepeatingHtmlList
              title="Apa saja yang akan dipelajari?"
              name="syllabus"
              idPrefix="inputsyllabusdescription"
              value={fields.syllabus.value}
              onAdd={() => form.addItem('syllabus')}
              onRemove={() => form.removeItem('syllabus')}
              onItemInput={form.handleItemInput}
            />
          )}
          {eventCategoryCheck && (
            <RepeatingHtmlList
              title="Untuk siapa acara ini ditujukan?"
              name="audience"
              idPrefix="inputaudiencedescription"
              value={fields.audience.value}
              onAdd={() => form.addItem('audience')}
              onRemove={() => form.removeItem('audience')}
              onItemInput={form.handleItemInput}
            />
          )}
          <SubmitButtons
            edit={edit}
            isLoading={isLoading}
            onSubmit={submitSchedule}
            onCancel={onCancel}
          />
        </div>
      </div>
      <PriceInputModal
        isOpen={priceInputModalOpen}
        toggle={() => setPriceInputModalOpen((prev) => !prev)}
        handleAddPrices={handleAddPrices}
        selectedPrice={selectedPrice}
        resetSelectedPrice={() => setSelectedPrice({})}
      />
      <PromoInputModal
        handleAddAdditionalPromos={handleAddAdditionalPromos}
        isOpen={promoInputModalOpen}
        toggle={() => setPromoInputModalOpen((prev) => !prev)}
        selectedPromo={selectedPromo}
        resetSelectedPromo={() => setSelectedPromo({})}
      />
    </div>
  );
};

export default ScheduleForm;
